import { isEmpty, isUndefined } from 'lodash'

import Button from '../common/Button/Button.component'
import CarrierLogo from '../common/CarrierLogo'
import EnrollmentRequestConfirmation from '../DashboardPage/Modals/EnrollmentRequestConfirmation'
import { MedicareQuotesType } from '../../enums/MedicareQuoteTypeEnum'
import ModalComponent from '../common/Modal/Modal.component'
import { Rating } from '@mui/material'
import React from 'react'
import customerStore from '../../datastore/CustomerStore'
import { getApiErrorMessage } from '../../utils/StringUtils'
import snackbarStore from '../../datastore/SnackbarStore'
import styled from '@emotion/styled'
import theme from '../../global/theme'
import { updateEnrollmentRequest } from '../../service/quotes'
import { updatePreferredNumberForEnrollRequestedPlan } from '../../service/customer'
import useEnrollmentApplicationModal from '../EnrollmentApplication/ConditionalModal'
import userActionStore from '../../datastore/UserActionStore'

interface CarouselCardProps {
  id: string
  carrier: string
  premium: number
  name: string
  favorite?: boolean
  enrollRequestStatus: boolean
  planType: MedicareQuotesType
  msPlanType?: string | undefined
  ridersApplied?: string[] | undefined
  onEnroll: (id: string) => void
  cardLoading?: boolean
  rating?: number
  moop?: number
  enrollmentRedirectURL?: string
  isEappEnabled?: boolean
  msEnrollmentApplicationKey?: string
  msSpouseEnrollmentApplicationKey?: string
}

const Container = styled.div`
  max-width: 760px;
  min-width: 350px;
  border: 1px solid ${theme.colors.borderColor};
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  @media screen and (max-width: 650px) {
    min-width: 250px;
  }
`
const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  div {
    display: contents;
    align-items: center;
    img {
      max-width: 109.5px;
      max-height: 33.75px;
    }
  }
`

const ContentWrapper = styled.div`
  text-align: center;
  h2 {
    font-size: 16px;
    color: ${theme.colors.textDark};
    font-weight: 700;
    padding-top: 9px;
    line-height: 22px;
    text-align: center;
  }

  p {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.colors.textGrey};
    padding-top: 9px;

    span {
      font-weight: 700;
      font-size: 14px;
      color: ${theme.colors.textDark};
    }
  }
`

const FooterWrapper = styled.div`
  padding-top: 24px;
  display: flex;
  justify-content: center;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
  div {
    display: flex;
    align-items: center;
    span {
      font-size: 14px;
      color: ${theme.colors.textGrey};
    }
  }
`

const CarouselCard: React.FC<CarouselCardProps> = ({
  id,
  name,
  premium,
  carrier,
  favorite = false,
  onEnroll,
  enrollRequestStatus,
  planType,
  msPlanType,
  ridersApplied,
  cardLoading = false,
  rating,
  moop,
  enrollmentRedirectURL,
  isEappEnabled = false,
  msEnrollmentApplicationKey,
  msSpouseEnrollmentApplicationKey,
}) => {
  const [enrollRequested, setEnrollRequested] =
    React.useState(enrollRequestStatus)
  const [enrollmentRequestPromptActive, setEnrollmentRequestPromptActive] =
    React.useState(false)
  const [preferredPhone, setPreferredPhone] = React.useState(
    customerStore.get().phone.replace('+1', '')
  )
  const [preferredPhoneLoading, setPreferredPhoneLoading] =
    React.useState(false)

  const { showEAppModal, EnrollmentApplicationModal } =
    useEnrollmentApplicationModal(
      msEnrollmentApplicationKey,
      msSpouseEnrollmentApplicationKey
    )

  const enrollBtnText =
    planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
      ? !isEappEnabled && (enrollRequested || enrollRequestStatus)
        ? 'Application Requested'
        : !isEmpty(msEnrollmentApplicationKey)
        ? 'Application Submitted'
        : 'APPLY'
      : enrollRequested || enrollRequestStatus
      ? 'Enrollment Requested'
      : 'ENROLL'
  const [loading, setLoading] = React.useState(false)

  const handleEnrollRequest = () => {
    setLoading(true)
    if (
      id &&
      planType === MedicareQuotesType.MEDICARE_SUPPLEMENT &&
      isEmpty(userActionStore.getRidersApplied(id)) &&
      !isEmpty(ridersApplied)
    ) {
      userActionStore.setRidersApplied(id, ridersApplied!)
    }

    const riders = id
      ? userActionStore.getRidersAppliedWithFullNames(id).length > 0
        ? userActionStore.getRidersAppliedWithFullNames(id)
        : undefined
      : undefined

    updateEnrollmentRequest(
      id,
      planType,
      false,
      `+1${preferredPhone.toString()}`,
      riders?.toString(),
      enrollRequested || enrollRequestStatus,
      isEappEnabled
    )
      .then(() => {
        setLoading(false)
        if (!isEappEnabled) {
          snackbarStore.set({
            snackbarOpen: true,
            snackbarMessage: `${
              planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                ? 'Apply'
                : 'Enroll'
            } request saved successfully`,
            snackbarType: 'success',
          })
          setEnrollRequested(true)
          setEnrollmentRequestPromptActive(true)
        }
        userActionStore.setEnrollmentRequestedPlan({
          planId: id,
          planType: planType,
        })
      })
      .catch((err) => {
        if (!isEappEnabled) {
          if (
            err.data?.message ===
            'Maximum limit reached for requesting enrollment for plans'
          ) {
            snackbarStore.set({
              snackbarOpen: true,
              snackbarMessage: getApiErrorMessage(
                `save ${
                  planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                    ? 'apply'
                    : 'enroll'
                } request. Maximum limit reached`
              ),
              snackbarType: 'error',
            })
          } else {
            snackbarStore.set({
              snackbarOpen: true,
              snackbarMessage: getApiErrorMessage(
                `save ${
                  planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                    ? 'apply'
                    : 'enroll'
                } request`
              ),
              snackbarType: 'error',
            })
          }
        }
        setLoading(false)
        setEnrollRequested(false)
        setEnrollmentRequestPromptActive(false)
      })
      .finally(() => {
        if (planType === MedicareQuotesType.MEDICARE_SUPPLEMENT) {
          showEAppModal({
            planId: id,
            forSpouse: false,
            isEappEnabled,
            planDetails: {
              carrier,
              planName: name,
              planType: msPlanType || '',
              monthlyPremium: premium,
            },
          })
        }
      })
  }

  const handlePreferredNumberForEnrollRequest = async () => {
    if (preferredPhone !== customerStore.get().phone.replace('+1', '')) {
      setPreferredPhoneLoading(true)
      updatePreferredNumberForEnrollRequestedPlan({
        'plan-id': id,
        'plan-type': planType,
        'preferred-number': preferredPhone,
      })
        .then(() => {
          snackbarStore.set({
            snackbarOpen: true,
            snackbarMessage: 'Successfully saved the preferred number',
            snackbarType: 'success',
          })
          setEnrollmentRequestPromptActive(false)
        })
        .catch((err) => {
          snackbarStore.set({
            snackbarOpen: true,
            snackbarMessage: 'Please try again',
            snackbarType: 'error',
          })
        })
        .finally(() => {
          setPreferredPhoneLoading(false)
        })
    } else {
      setEnrollmentRequestPromptActive(false)
    }

    onEnroll(id)
  }

  return (
    <Container>
      {/* <div style={{ position: 'absolute', top: 0, right: 0 }}>
        <CloseIconButton onClick={() => onRemove(id)} />
      </div> */}
      {enrollmentRequestPromptActive && (
        <ModalComponent
          setOpen={() => {
            setEnrollmentRequestPromptActive(false)
          }}
          description='Popup - Request for Enrollment Confirmation'
          hideCloseButton={true}
        >
          <EnrollmentRequestConfirmation
            setOpen={() => {
              setEnrollmentRequestPromptActive(false)
            }}
            onEnroll={() => {
              handlePreferredNumberForEnrollRequest()
            }}
            planName={name}
            planId={id}
            planType={planType}
            buttonLoading={preferredPhoneLoading}
            preferredPhoneNumber={preferredPhone}
            setPreferredPhoneNumber={setPreferredPhone}
            enrollmentRedirectURL={enrollmentRedirectURL}
          />
        </ModalComponent>
      )}
      <LogoWrapper>
        <div>
          <CarrierLogo carrier={carrier} />
        </div>
      </LogoWrapper>
      <ContentWrapper>
        <h2>{name}</h2>

        {msPlanType && msPlanType !== '' && (
          <p
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                color: '#666666',
                fontWeight: '600',
                fontSize: '14px',
                marginRight: '5px',
                lineHeight: '18px',
              }}
            >
              Plan Type:
            </span>
            <span
              style={{
                fontSize: '14px',
                color: `#2F3032`,
                fontWeight: 700,
                lineHeight: '18px',
              }}
            >
              {msPlanType}
            </span>
          </p>
        )}
        <p
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              color: '#666666',
              fontWeight: '600',
              fontSize: '14px',
              marginRight: '5px',
              lineHeight: '18px',
            }}
          >
            Monthly Premium:
          </span>
          <span
            style={{
              fontSize: '20px',
              color: theme.colors.primary,
              fontWeight: 700,
              lineHeight: '28px',
            }}
          >
            ${premium.toFixed(2)}
          </span>
        </p>

        {!isUndefined(moop) && (
          <p
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                color: '#666666',
                fontWeight: '600',
                fontSize: '14px',
                marginRight: '5px',
                lineHeight: '18px',
              }}
            >
              Maximum out of pocket:
            </span>
            <span
              style={{
                fontSize: '14px',
                color: `#2F3032`,
                fontWeight: 700,
                lineHeight: '18px',
              }}
            >
              ${moop.toFixed(2)}
            </span>
          </p>
        )}
        {planType !== MedicareQuotesType.MEDICARE_SUPPLEMENT && rating && (
          <Rating
            name='simple-controlled'
            value={rating || 0}
            precision={0.5}
            readOnly={true}
            sx={{
              marginTop: '1rem',
            }}
          />
        )}
        <FooterWrapper>
          <div>
            <Button
              width='150px'
              variant='contained'
              color='secondary'
              bold={true}
              height='30px'
              onClick={() => {
                if (!isEmpty(enrollmentRedirectURL) && !isEappEnabled)
                  window.open(enrollmentRedirectURL, '_blank')
                handleEnrollRequest()
              }}
              disabled={
                cardLoading ||
                (planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                  ? (!isEappEnabled &&
                      (enrollRequested || enrollRequestStatus)) ||
                    !isEmpty(msEnrollmentApplicationKey)
                  : enrollRequested || enrollRequestStatus)
              }
              additionalPadding={true}
              description={`Attempted Enrollment in ${name}`}
              id={`compareEnroll ${name}`}
              loading={loading}
            >
              {enrollBtnText}
            </Button>
          </div>
        </FooterWrapper>
      </ContentWrapper>
      {planType === MedicareQuotesType.MEDICARE_SUPPLEMENT && (
        <EnrollmentApplicationModal />
      )}
    </Container>
  )
}

export default CarouselCard
